import { useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function getSessionId() {
  let sid = sessionStorage.getItem("_bsid");
  if (!sid) { sid = Math.random().toString(36).slice(2) + Date.now().toString(36); sessionStorage.setItem("_bsid", sid); }
  return sid;
}

export function trackEvent(eventType, value = "", category = "", element = "") {
  const sid = getSessionId();
  axios.post(`${API}/track/behavior`, { event_type: eventType, page: window.location.pathname, value, category, element, session_id: sid }).catch(() => {});
}

export default function BehaviorTracker() {
  const location = useLocation();
  const enterTime = useRef(Date.now());
  const maxScroll = useRef(0);
  const tracked = useRef(false);

  const sendExit = useCallback(() => {
    if (tracked.current) return;
    tracked.current = true;
    const duration = Math.round((Date.now() - enterTime.current) / 1000);
    trackEvent("page_exit", location.pathname, "", `${duration}s`);
    if (maxScroll.current > 0) {
      axios.post(`${API}/track/behavior`, {
        event_type: "scroll_depth", page: location.pathname,
        scroll_depth: maxScroll.current, duration_seconds: duration,
        session_id: getSessionId()
      }).catch(() => {});
    }
  }, [location.pathname]);

  useEffect(() => {
    enterTime.current = Date.now();
    maxScroll.current = 0;
    tracked.current = false;

    // Track page view
    axios.post(`${API}/track/visit`, { page: location.pathname, referrer: document.referrer }).catch(() => {});

    // Scroll tracking
    const onScroll = () => {
      const depth = Math.round((window.scrollY / (document.body.scrollHeight - window.innerHeight)) * 100);
      if (depth > maxScroll.current) maxScroll.current = depth;
    };

    // Click tracking on interactive elements
    const onClick = (e) => {
      const el = e.target.closest("[data-testid], a, button");
      if (!el) return;
      const testId = el.getAttribute("data-testid") || "";
      const text = (el.textContent || "").trim().slice(0, 50);
      const href = el.getAttribute("href") || "";

      // Categorize clicks
      if (testId.startsWith("buy-") || testId.startsWith("purchase")) {
        trackEvent("product_click", text, "purchase", testId);
      } else if (testId.startsWith("apply-")) {
        trackEvent("gig_apply_click", text, "gig", testId);
      } else if (testId.includes("book") || href.includes("booking")) {
        trackEvent("booking_interest", text, "conversion", testId);
      } else if (testId.includes("whatsapp") || href.includes("wa.me")) {
        trackEvent("whatsapp_click", text, "contact", testId);
      } else if (testId.startsWith("filter-") || testId.startsWith("gig-filter")) {
        trackEvent("filter_click", text, "browse", testId);
      } else if (testId.startsWith("product-")) {
        trackEvent("product_view", text, "shop", testId);
      } else if (testId.startsWith("tier-")) {
        trackEvent("license_tier_click", text, "marketplace", testId);
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("click", onClick);

    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("click", onClick);
      sendExit();
    };
  }, [location.pathname, sendExit]);

  // Track exit on page unload
  useEffect(() => {
    const onBeforeUnload = () => sendExit();
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [sendExit]);

  return null;
}
